// pages/Website.js - Complete with Cherry Theme
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProducts } from '../hooks/useProducts';
import { useOrders } from '../hooks/useOrders';
import { useCart } from '../contexts/CartContext';
import Header from '../components/website/Header';
import CartDrawer from '../components/website/CartDrawer';
import CheckoutPage from '../components/website/CheckoutPage';
import ProfilePage from '../components/website/ProfilePage';
import DecorationEnquiryPage from '../components/website/DecorationEnquiryPage';
import Footer from '../components/website/Footer';
import { dbOnValue, dbGet } from '../config/firebase';
import { 
  Search, X, ShoppingCart, Home, User, ChevronRight, ChevronLeft, 
  Flower2, Gift, Truck, Shield, Clock, Award, Heart, Zap,
  Sparkles, Crown, Leaf, Package, Palette,
  Plus, Minus, ShoppingBag, Share2, AlertCircle, Info, CheckCircle, Grid
} from 'lucide-react';

// Custom Auto-close Centered Dialog Component
const CustomDialog = ({ isOpen, onClose, message, type, productName }) => {
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getIcon = () => {
    switch(type) {
      case 'cart':
        return <ShoppingBag size={48} color="#dc2626" />;
      case 'wishlist':
        return <Heart size={48} color="#dc2626" fill="#dc2626" />;
      case 'share':
        return <Share2 size={48} color="#dc2626" />;
      case 'error':
        return <AlertCircle size={48} color="#ef4444" />;
      case 'info':
        return <Info size={48} color="#dc2626" />;
      default:
        return <Sparkles size={48} color="#dc2626" />;
    }
  };

  const getTitle = () => {
    switch(type) {
      case 'cart':
        return 'Added to Cart!';
      case 'wishlist':
        return 'Wishlist Updated';
      case 'share':
        return 'Link Copied!';
      case 'error':
        return 'Oops!';
      case 'info':
        return 'Information';
      default:
        return 'Success!';
    }
  };

  return (
    <>
      <div 
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 2000,
          backdropFilter: 'blur(4px)',
          animation: 'fadeIn 0.2s ease'
        }}
      />
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'white',
        borderRadius: '24px',
        zIndex: 2001,
        width: '90%',
        maxWidth: '340px',
        padding: '28px 20px',
        textAlign: 'center',
        boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
        animation: 'scaleIn 0.2s ease'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          margin: '0 auto 16px',
          borderRadius: '50%',
          background: '#fff5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {getIcon()}
        </div>

        <h3 style={{
          fontSize: '20px',
          fontWeight: 700,
          color: '#1a0a0a',
          marginBottom: '8px'
        }}>
          {getTitle()}
        </h3>

        <p style={{
          fontSize: '14px',
          color: '#64748b',
          lineHeight: '1.5',
          marginBottom: productName ? '12px' : '0'
        }}>
          {message}
        </p>

        {productName && (
          <div style={{
            background: '#fff5f5',
            padding: '8px 12px',
            borderRadius: '12px',
            marginTop: '12px',
            border: '1px solid rgba(220,38,38,0.15)',
            display: 'inline-block'
          }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#dc2626' }}>
              {productName}
            </span>
          </div>
        )}

        <div style={{
          width: '60px',
          height: '3px',
          background: '#e2e8f0',
          borderRadius: '3px',
          margin: '20px auto 0',
          overflow: 'hidden'
        }}>
          <div style={{
            width: '100%',
            height: '100%',
            background: '#dc2626',
            borderRadius: '3px',
            animation: 'shrink 2s linear forwards'
          }} />
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
        @keyframes shrink {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
      `}</style>
    </>
  );
};

// Hero Banner Component with Cherry Theme
const HeroBanner = ({ banners, currentSlide, goToSlide, nextSlide, prevSlide, isMobile, navigate }) => {
  const getPositionStyle = (banner) => {
    const position = banner.textPosition || 'bottom-left';
    const baseStyle = {
      position: 'absolute',
      zIndex: 2,
      maxWidth: '85%'
    };
    
    switch(position) {
      case 'bottom-left':
        return { ...baseStyle, bottom: isMobile ? '12px' : '20px', left: isMobile ? '16px' : '30px', textAlign: 'left' };
      case 'bottom-right':
        return { ...baseStyle, bottom: isMobile ? '12px' : '20px', right: isMobile ? '16px' : '30px', textAlign: 'right' };
      case 'top-left':
        return { ...baseStyle, top: isMobile ? '12px' : '20px', left: isMobile ? '16px' : '30px', textAlign: 'left' };
      case 'top-right':
        return { ...baseStyle, top: isMobile ? '12px' : '20px', right: isMobile ? '16px' : '30px', textAlign: 'right' };
      case 'center':
        return { ...baseStyle, top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' };
      default:
        return { ...baseStyle, bottom: isMobile ? '12px' : '20px', left: isMobile ? '16px' : '30px', textAlign: 'left' };
    }
  };

  const handleShopNow = (banner) => {
    if (banner.buttonLink) {
      navigate(banner.buttonLink);
    }
  };

  if (!banners || banners.length === 0) return null;

  return (
    <div style={{ 
      position: 'relative', 
      width: '100%', 
      height: isMobile ? '200px' : '320px', 
      overflow: 'hidden', 
      borderRadius: '16px', 
      marginBottom: '20px', 
      cursor: 'pointer',
      boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
    }}>
      <div style={{ 
        display: 'flex', 
        width: '100%', 
        height: '100%', 
        transition: 'transform 0.5s ease-in-out', 
        transform: `translateX(-${currentSlide * 100}%)` 
      }}>
        {banners.map((banner, idx) => {
          let overlayOpacity = banner.overlayOpacity;
          if (overlayOpacity === undefined || overlayOpacity === null) {
            overlayOpacity = 0.1;
          }
          overlayOpacity = Math.min(Math.max(parseFloat(overlayOpacity), 0), 0.6);
          
          const backgroundStyle = overlayOpacity > 0 
            ? `linear-gradient(rgba(0,0,0,${overlayOpacity}), rgba(0,0,0,${overlayOpacity})), url(${banner.image})`
            : `url(${banner.image})`;
          
          return (
            <div 
              key={idx} 
              style={{ 
                minWidth: '100%', 
                height: '100%', 
                position: 'relative',
                backgroundImage: backgroundStyle,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <div style={getPositionStyle(banner)}>
                {banner.showTitle !== false && banner.title && (
                  <h2 style={{ 
                    fontSize: isMobile ? '14px' : '20px', 
                    fontWeight: 700, 
                    marginBottom: '4px',
                    color: banner.titleColor || '#ffffff',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                    lineHeight: '1.3'
                  }}>
                    {banner.title}
                  </h2>
                )}
                
                {banner.showSubtitle !== false && banner.subtitle && (
                  <p style={{ 
                    fontSize: isMobile ? '9px' : '12px', 
                    marginBottom: '6px',
                    color: banner.subtitleColor || '#e2e8f0',
                    textShadow: '1px 1px 1px rgba(0,0,0,0.3)',
                    opacity: 0.95,
                    lineHeight: '1.4',
                    maxWidth: isMobile ? '200px' : '350px'
                  }}>
                    {banner.subtitle}
                  </p>
                )}
                
                {banner.showButton !== false && banner.buttonText && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleShopNow(banner); }}
                    style={{
                      display: 'inline-block',
                      padding: isMobile ? '5px 12px' : '8px 20px',
                      background: banner.buttonBgColor || '#dc2626',
                      color: banner.buttonTextColor || '#ffffff',
                      borderRadius: '25px',
                      fontSize: isMobile ? '10px' : '13px',
                      fontWeight: 600,
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'transform 0.2s, background 0.2s',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.02)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    {banner.buttonText} →
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {banners.length > 1 && (
        <>
          <button 
            onClick={(e) => { e.stopPropagation(); prevSlide(); }} 
            style={{ 
              position: 'absolute', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              left: 12, 
              background: 'rgba(0,0,0,0.4)', 
              border: 'none', 
              width: '32px', 
              height: '32px', 
              cursor: 'pointer', 
              borderRadius: '50%', 
              color: 'white', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              zIndex: 2,
              backdropFilter: 'blur(4px)'
            }}
          >
            <ChevronLeft size={18} />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); nextSlide(); }} 
            style={{ 
              position: 'absolute', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              right: 12, 
              background: 'rgba(0,0,0,0.4)', 
              border: 'none', 
              width: '32px', 
              height: '32px', 
              cursor: 'pointer', 
              borderRadius: '50%', 
              color: 'white', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              zIndex: 2,
              backdropFilter: 'blur(4px)'
            }}
          >
            <ChevronRight size={18} />
          </button>
        </>
      )}
      
      {banners.length > 1 && (
        <div style={{ 
          position: 'absolute', 
          bottom: 12, 
          left: '50%', 
          transform: 'translateX(-50%)', 
          display: 'flex', 
          gap: 8, 
          zIndex: 2 
        }}>
          {banners.map((_, idx) => (
            <button 
              key={idx} 
              onClick={() => goToSlide(idx)} 
              style={{ 
                width: currentSlide === idx ? '20px' : '6px', 
                height: '4px', 
                borderRadius: '2px', 
                background: currentSlide === idx ? '#dc2626' : 'rgba(255,255,255,0.6)', 
                cursor: 'pointer', 
                border: 'none',
                transition: 'all 0.2s'
              }} 
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Category Card - Cherry Theme
const CategoryCard = ({ category, onClick, isMobile }) => {
  if (!category) return null;
  
  return (
    <div 
      onClick={() => onClick(category)} 
      style={{ 
        textAlign: 'center', 
        cursor: 'pointer', 
        background: 'white', 
        borderRadius: '12px', 
        padding: isMobile ? '10px 6px' : '16px 12px', 
        border: '1px solid rgba(220,38,38,0.1)', 
        transition: 'all 0.2s ease', 
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
      }}
      onMouseEnter={(e) => { 
        e.currentTarget.style.transform = 'translateY(-3px)'; 
        e.currentTarget.style.boxShadow = '0 6px 16px rgba(220,38,38,0.12)'; 
        e.currentTarget.style.borderColor = '#dc2626'; 
      }}
      onMouseLeave={(e) => { 
        e.currentTarget.style.transform = 'translateY(0)'; 
        e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)'; 
        e.currentTarget.style.borderColor = 'rgba(220,38,38,0.1)'; 
      }}
    >
      <div style={{ 
        width: isMobile ? '40px' : '55px', 
        height: isMobile ? '40px' : '55px', 
        margin: '0 auto 8px', 
        borderRadius: '50%', 
        background: '#fff5f5', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <span style={{ fontSize: isMobile ? '24px' : '30px' }}>{category.icon || ''}</span>
      </div>
      <p style={{ fontSize: isMobile ? '10px' : '13px', fontWeight: 500, color: '#1a0a0a', marginBottom: '4px' }}>
        {category.name}
      </p>
      <p style={{ fontSize: isMobile ? '8px' : '10px', color: '#dc2626', fontWeight: 500 }}>
        {category.productCount || 0} items
      </p>
    </div>
  );
};

// Fresh Flower Card - Cherry Theme
const FreshFlowerCard = ({ subcategory, onClick, productCount, isMobile }) => {
  if (!subcategory) return null;
  
  return (
    <div 
      onClick={() => onClick(subcategory)} 
      style={{ 
        textAlign: 'center', 
        cursor: 'pointer', 
        transition: 'all 0.2s ease', 
        padding: isMobile ? '6px' : '12px' 
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div style={{ 
        width: isMobile ? '55px' : '75px', 
        height: isMobile ? '55px' : '75px', 
        margin: '0 auto 8px', 
        borderRadius: '50%', 
        background: 'linear-gradient(135deg, #fff5f5, #fff)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        border: '2px solid rgba(220,38,38,0.15)', 
        overflow: 'hidden', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)' 
      }}>
        <Flower2 size={isMobile ? 28 : 36} color="#dc2626" />
      </div>
      <p style={{ fontSize: isMobile ? '10px' : '12px', fontWeight: 500, color: '#1a0a0a' }}>
        {subcategory.name}
      </p>
      <p style={{ fontSize: isMobile ? '8px' : '10px', color: '#dc2626' }}>
        {productCount} items
      </p>
    </div>
  );
};

// Horizontal Product Card - Cherry Theme
const HorizontalProductCard = ({ product, onAddToCart, onBuyNow }) => {
  const navigate = useNavigate();
  
  if (!product) return null;
  
  const discountPercent = product.oldPrice && product.oldPrice > product.price 
    ? Math.round(((product.oldPrice - product.price) / product.oldPrice) * 100) 
    : 0;
  const isOutOfStock = product.stock === false || product.stock === 'false' || product.outOfStock === true;

  const handleCardClick = (e) => {
    if (e.target.closest('button')) {
      e.stopPropagation();
      return;
    }
    navigate(`/product/${product.id}`);
  };

  const handleAddClick = (e) => {
    e.stopPropagation();
    if (isOutOfStock) {
      
      return;
    }
    const productWithQuantity = { ...product, quantity: 1 };
    onAddToCart(productWithQuantity, e);
  };

  const handleBuyClick = (e) => {
    e.stopPropagation();
    if (isOutOfStock) {
     
      return;
    }
    const productWithQuantity = { ...product, quantity: 1 };
    onBuyNow(productWithQuantity, e);
  };

  return (
    <div 
      onClick={handleCardClick}
      style={{
        flex: '0 0 auto',
        width: '160px',
        background: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        border: isOutOfStock ? '1px solid #ef4444' : '1px solid rgba(220,38,38,0.1)',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        position: 'relative',
        opacity: isOutOfStock ? 0.6 : 1
      }}
      onMouseEnter={(e) => {
        if (!isOutOfStock) {
          e.currentTarget.style.transform = 'translateY(-3px)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(220,38,38,0.12)';
          e.currentTarget.style.borderColor = '#dc2626';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = isOutOfStock ? '#ef4444' : 'rgba(220,38,38,0.1)';
      }}
    >
      <div style={{ position: 'relative', height: '140px', background: '#fefaf8' }}>
        <img 
          src={product.image} 
          alt={product.name} 
          style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
          onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1591886960571-74d43a9d4166?w=200&q=80'}
        />
        {discountPercent > 0 && !isOutOfStock && (
          <div style={{ 
            position: 'absolute', 
            top: '6px', 
            left: '6px', 
            background: '#dc2626', 
            color: 'white', 
            padding: '2px 6px', 
            borderRadius: '4px', 
            fontSize: '9px', 
            fontWeight: 700 
          }}>
            {discountPercent}% OFF
          </div>
        )}
        {isOutOfStock && (
          <div style={{ 
            position: 'absolute', 
            top: '6px', 
            left: '6px', 
            background: '#ef4444', 
            color: 'white', 
            padding: '2px 6px', 
            borderRadius: '4px', 
            fontSize: '9px', 
            fontWeight: 700 
          }}>
            Out of Stock
          </div>
        )}
        <button 
          onClick={(e) => { e.stopPropagation(); }}
          style={{ 
            position: 'absolute', 
            top: '6px', 
            right: '6px', 
            background: 'white', 
            border: 'none', 
            borderRadius: '50%', 
            width: '26px', 
            height: '26px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            cursor: 'pointer', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)' 
          }}
        >
          <Heart size={12} color="#999" />
        </button>
      </div>

      <div style={{ padding: '10px' }}>
        <h3 style={{ 
          fontSize: '11px', 
          fontWeight: 500, 
          margin: '0 0 4px', 
          color: isOutOfStock ? '#94a3b8' : '#1a0a0a', 
          lineHeight: '1.3',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          minHeight: '28px'
        }}>
          {product.name}
        </h3>

        <div style={{ marginBottom: '8px' }}>
          <span style={{ fontSize: '14px', fontWeight: 700, color: '#dc2626' }}>
            ₹{(product.price || 0).toLocaleString()}
          </span>
          {product.oldPrice && (
            <span style={{ fontSize: '10px', color: '#94a3b8', textDecoration: 'line-through', marginLeft: '6px' }}>
              ₹{product.oldPrice.toLocaleString()}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: '6px' }}>
          <button 
            onClick={handleAddClick}
            disabled={isOutOfStock}
            style={{ 
              flex: 1, 
              padding: '6px 8px', 
              background: isOutOfStock ? '#e2e8f0' : '#ff9f00', 
              color: isOutOfStock ? '#94a3b8' : 'white', 
              border: 'none', 
              borderRadius: '6px', 
              fontSize: '9px', 
              fontWeight: 600, 
              cursor: isOutOfStock ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!isOutOfStock) e.currentTarget.style.background = '#f0a500';
            }}
            onMouseLeave={(e) => {
              if (!isOutOfStock) e.currentTarget.style.background = '#ff9f00';
            }}
          >
            Add
          </button>
          <button 
            onClick={handleBuyClick}
            disabled={isOutOfStock}
            style={{ 
              flex: 1, 
              padding: '6px 8px', 
              background: isOutOfStock ? '#e2e8f0' : '#fb641b', 
              color: isOutOfStock ? '#94a3b8' : 'white', 
              border: 'none', 
              borderRadius: '6px', 
              fontSize: '9px', 
              fontWeight: 600, 
              cursor: isOutOfStock ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!isOutOfStock) e.currentTarget.style.background = '#f35b1a';
            }}
            onMouseLeave={(e) => {
              if (!isOutOfStock) e.currentTarget.style.background = '#fb641b';
            }}
          >
            Buy
          </button>
        </div>
      </div>
    </div>
  );
};

// Horizontal Product Section
const HorizontalProductSection = ({ title, products, onAddToCart, onBuyNow, isMobile }) => {
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  
  // Filter out out-of-stock products
  const availableProducts = (products || []).filter(p => p && p.stock !== false && p.outOfStock !== true);
  
  const scroll = useCallback((dir) => { 
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir === 'left' ? -300 : 300, behavior: 'smooth' }); 
    }
  }, []);
  
  const checkScroll = useCallback(() => { 
    if (scrollRef.current) { 
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current; 
      setShowLeftArrow(scrollLeft > 20); 
      setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 20); 
    } 
  }, []);
  
  useEffect(() => { 
    const ref = scrollRef.current; 
    if (ref) { 
      ref.addEventListener('scroll', checkScroll); 
      checkScroll(); 
      return () => ref.removeEventListener('scroll', checkScroll); 
    } 
  }, [checkScroll]);
  
  if (!availableProducts || availableProducts.length === 0) {
    return null;
  }

  return (
    <div style={{ marginBottom: '28px', position: 'relative' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '14px', 
        padding: '0 4px' 
      }}>
        <h2 style={{ 
          fontSize: isMobile ? '16px' : '20px', 
          fontWeight: 700, 
          margin: 0, 
          color: '#1a0a0a',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: isMobile ? '18px' : '22px' }}></span>
          {title}
        </h2>
      </div>
      <div style={{ position: 'relative' }}>
      
        
        <div 
          ref={scrollRef} 
          style={{ 
            display: 'flex', 
            overflowX: 'auto', 
            scrollBehavior: 'smooth', 
            gap: '14px', 
            padding: '4px 4px 8px 4px', 
            scrollbarWidth: 'none', 
            msOverflowStyle: 'none' 
          }} 
          className="hide-scrollbar"
        >
          {availableProducts.slice(0, 15).map(product => (
            <HorizontalProductCard 
              key={product.id} 
              product={product} 
              onAddToCart={onAddToCart} 
              onBuyNow={onBuyNow} 
            />
          ))}
        </div>
        
        
      </div>
    </div>
  );
};

// Main Website Component
const Website = () => {
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showDecorationEnquiry, setShowDecorationEnquiry] = useState(false);
  const [profileDefaultTab, setProfileDefaultTab] = useState('profile');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [banners, setBanners] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState({});
  const [freshFlowerSubs, setFreshFlowerSubs] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);

  // Dialog state
  const [dialog, setDialog] = useState({
    isOpen: false,
    message: '',
    type: 'cart',
    productName: null
  });

  const showDialog = (message, type = 'cart', productName = null) => {
    setDialog({ isOpen: true, message, type, productName });
  };

  const closeDialog = () => {
    setDialog({ isOpen: false, message: '', type: 'cart', productName: null });
  };

  const { user } = useAuth();
  const { products: allProducts, loading: productsLoading } = useProducts();
  const { placeOrder } = useOrders();
  const { cart, addToCart } = useCart();
  const navigate = useNavigate();

  // Get products array from the products object
  const getProductsArray = useCallback(() => {
    if (!allProducts) return [];
    // If allProducts is an array, return it
    if (Array.isArray(allProducts)) return allProducts;
    // If allProducts is an object with categories, flatten it
    if (typeof allProducts === 'object') {
      const flatArray = [];
      Object.values(allProducts).forEach(category => {
        if (Array.isArray(category)) {
          flatArray.push(...category);
        }
      });
      return flatArray;
    }
    return [];
  }, [allProducts]);

  const productsArray = getProductsArray();
  
  // Filter only in-stock products for display
  const inStockProducts = useMemo(() => {
    return productsArray.filter(p => p && p.stock !== false && p.outOfStock !== true);
  }, [productsArray]);

  // Load categories & subcategories
  useEffect(() => {
    const loadFirebaseData = async () => {
      try {
        const cats = await dbGet('shop_categories');
        let catsArray = [];
        if (cats && Array.isArray(cats)) {
          catsArray = cats;
        } else if (cats && typeof cats === 'object') {
          catsArray = Object.values(cats);
        }
        
        if (catsArray.length === 0) {
          catsArray = [
            { id: 'cat_1', name: 'Bouquets', icon: '💐' },
            { id: 'cat_2', name: 'Fresh Flowers', icon: '🌸' },
            { id: 'cat_3', name: 'Gift Hampers', icon: '🎁' },
            { id: 'cat_4', name: 'Plants', icon: '🪴' },
          ];
        }
        
        // Add product counts to categories
        const catsWithCount = catsArray.map(cat => ({
          ...cat,
          productCount: productsArray.filter(p => p.category === cat.id || p.mainCategory === cat.id).length
        }));
        
        setCategories(catsWithCount);

        const subs = await dbGet('shop_subcategories');
        let subsObj = subs || {};
        setSubcategories(subsObj);
        
        const freshCat = catsArray.find(c => c.name === 'Fresh Flowers');
        if (freshCat && subsObj[freshCat.id]) {
          setFreshFlowerSubs(subsObj[freshCat.id]);
        } else {
          setFreshFlowerSubs([]);
        }
        
      // Load gallery images - Updated to handle new format
const gallery = await dbGet('decoration_gallery');
if (gallery && Object.keys(gallery).length > 0) {
  const galleryList = Object.entries(gallery).map(([key, img]) => ({
    id: key,
    ...img,
    // Ensure images is always an array
    images: img.images || (img.url ? [{ url: img.url }] : [])
  })).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  setGalleryImages(galleryList.slice(0, 4));
} else {
  setGalleryImages([
    { id: 'default_1', title: 'Birthday Decor', images: [{ url: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=300' }] },
    { id: 'default_2', title: 'Wedding Decor', images: [{ url: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=300' }] },
    { id: 'default_3', title: 'Anniversary Decor', images: [{ url: 'https://images.unsplash.com/photo-1513278974582-3e1b4a4fa21a?w=300' }] },
    { id: 'default_4', title: 'Baby Shower', images: [{ url: 'https://images.unsplash.com/photo-1519225495810-7512c696505a?w=300' }] }
  ]);
}
      } catch (err) { 
        console.error(err); 
      }
    };
    loadFirebaseData();
  }, [productsArray]);

  // Fetch banners
  useEffect(() => {
    const unsubscribe = dbOnValue('banners', (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const bannerList = Object.entries(data)
          .map(([k, v]) => ({ id: k, ...v }))
          .filter(b => b.active !== false)
          .sort((a,b) => (a.order||0)-(b.order||0));
        setBanners(bannerList);
      }
    });
    return () => unsubscribe();
  }, []);

  // Auto slide for banners
  useEffect(() => { 
    if (banners.length > 1) { 
      const i = setInterval(() => setCurrentSlide(prev => (prev+1)%banners.length), 5000); 
      return () => clearInterval(i); 
    } 
  }, [banners.length]);

  // Handle resize
  useEffect(() => { 
    const handleResize = () => setIsMobile(window.innerWidth <= 768); 
    handleResize(); 
    window.addEventListener('resize', handleResize); 
    return () => window.removeEventListener('resize', handleResize); 
  }, []);

  // Get random products from in-stock products only
  const getRandomProducts = useCallback((count = 10) => {
    if (inStockProducts.length === 0) return [];
    const shuffled = [...inStockProducts].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  }, [inStockProducts]);

  const featuredProducts = useMemo(() => getRandomProducts(10), [getRandomProducts]);
  const bestSellerProducts = useMemo(() => getRandomProducts(10), [getRandomProducts]);
  const newArrivalProducts = useMemo(() => getRandomProducts(10), [getRandomProducts]);
  
  // Search results from in-stock products only
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return inStockProducts.filter(p => 
      p && (p.name?.toLowerCase().includes(query) || 
      p.description?.toLowerCase().includes(query) || 
      p.tag?.toLowerCase().includes(query))
    );
  }, [inStockProducts, searchQuery]);

  const goToSlide = (i) => setCurrentSlide(i);
  const nextSlide = () => setCurrentSlide(prev => (prev+1)%banners.length);
  const prevSlide = () => setCurrentSlide(prev => (prev-1+banners.length)%banners.length);
  
  const handleSearch = (q) => { 
    if (q && q.trim()) { 
      setSearchQuery(q); 
      setShowSearchResults(true); 
    } else {
      setSearchQuery('');
      setShowSearchResults(false);
    }
  };
  
  const clearSearch = () => { 
    setSearchQuery(''); 
    setShowSearchResults(false); 
  };
  
  const handleAddToCart = (product, e) => { 
    if(e) e.stopPropagation(); 
    if(!user){ 
      showDialog('Please login to add items to cart', 'info');
      setTimeout(() => navigate('/login'), 2000);
      return; 
    } 
    addToCart({ 
      id: product.id, 
      name: product.name, 
      price: product.price, 
      image: product.image, 
      quantity: product.quantity || 1, 
      variantId: 'standard', 
      variantName: 'Standard' 
    }); 
    showDialog(`${product.name} added to cart!`, 'cart', product.name);
  };
  
  const handleBuyNow = (product, e) => { 
    if(e) e.stopPropagation(); 
    if(!user){ 
      showDialog('Please login to continue', 'info');
      setTimeout(() => navigate('/login'), 2000);
      return; 
    } 
    sessionStorage.setItem('buyNowProduct', JSON.stringify({ 
      items: [{ ...product, quantity: 1 }], 
      totalAmount: product.price 
    })); 
    navigate('/checkout'); 
  };
  
  const handlePlaceOrder = async (orderData) => { 
    const order = await placeOrder(orderData); 
    setShowCheckout(false); 
    return order; 
  };
  
  const handleCategoryClick = (cat) => navigate(`/category/${cat.id}`, { state: { category: cat } });
  
  const handleFreshFlowerClick = (sub) => { 
    const freshCat = categories.find(c => c.name === 'Fresh Flowers'); 
    if (freshCat) {
      navigate(`/category/${freshCat.id}`, { state: { category: freshCat, selectedSubcategory: sub } });
    } else {
      showDialog('Fresh Flowers category not found', 'error');
    }
  };
  
  const handleAllCategories = () => navigate('/all-categories');
  const handleDecorationGallery = () => navigate('/decoration-gallery');
  const handleDecorationEnquiry = () => navigate('/decoration-enquiry');
  const handleBottomDecorationEnquiry = () => setShowDecorationEnquiry(true);
  const handleBottomProfile = () => { 
    if(user) { 
      setProfileDefaultTab('profile'); 
      setShowProfile(true); 
    } else { 
      navigate('/login'); 
    } 
  };

  const isLoading = productsLoading;

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#fff5f5', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div className="spinner" style={{ width: 40, height: 40, border: '3px solid #dc2626', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ background: '#fff5f5', minHeight: '100vh' }}>
      {/* Custom Dialog */}
      <CustomDialog 
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        message={dialog.message}
        type={dialog.type}
        productName={dialog.productName}
      />

      <Header 
        onCartClick={() => setShowCart(true)} 
        onProfileClick={() => { setProfileDefaultTab('profile'); setShowProfile(true); }} 
        onSearch={handleSearch} 
        searchQuery={searchQuery} 
      />
      
      {!showSearchResults ? (
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: isMobile ? '12px' : '20px', paddingBottom: isMobile ? '70px' : '0' }}>
          {/* Hero Banner */}
          <HeroBanner 
            banners={banners} 
            currentSlide={currentSlide} 
            goToSlide={goToSlide} 
            nextSlide={nextSlide} 
            prevSlide={prevSlide} 
            isMobile={isMobile}
            navigate={navigate}
          />
          
          {/* Shop by Category */}
          <div style={{ background: 'white', borderRadius: '16px', padding: isMobile ? '14px' : '20px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ fontSize: isMobile ? '14px' : '18px', fontWeight: 700, color: '#1a0a0a' }}>Shop by Category</h3>
              <button 
                onClick={handleAllCategories}
                style={{ color: '#fff', fontSize: isMobile ? '13px' : '13px', background: '#dc2626', border: '0.2px solid rgb(232, 150, 150)', cursor: 'pointer', fontWeight: 800,padding:'4px',borderRadius:'5px' }}
              >
                View All 
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(4, 1fr)' : 'repeat(8, 1fr)', gap: isMobile ? '8px' : '16px' }}>
              {categories.slice(0, 8).map(cat => (
                <CategoryCard key={cat.id} category={cat} onClick={handleCategoryClick} isMobile={isMobile} />
              ))}
            </div>
          </div>
          
          {/* Product Sections - Only show if there are products */}
          {featuredProducts.length > 0 && (
            <HorizontalProductSection 
              title="Featured for You" 
              products={featuredProducts} 
              onAddToCart={handleAddToCart} 
              onBuyNow={handleBuyNow} 
              isMobile={isMobile} 
            />
          )}
          
          {bestSellerProducts.length > 0 && (
            <HorizontalProductSection 
              title="Best Sellers" 
              products={bestSellerProducts} 
              onAddToCart={handleAddToCart} 
              onBuyNow={handleBuyNow} 
              isMobile={isMobile} 
            />
          )}
          
          {/* Fresh Flowers */}
          {freshFlowerSubs.length > 0 && (
            <div style={{ background: 'white', borderRadius: '16px', padding: isMobile ? '14px' : '20px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ fontSize: isMobile ? '14px' : '18px', fontWeight: 700, color: '#1a0a0a' }}>Fresh Flowers</h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(4, 1fr)' : 'repeat(8, 1fr)', gap: isMobile ? '8px' : '16px' }}>
                {freshFlowerSubs.slice(0, 8).map(sub => {
                  const count = productsArray.filter(p => p.subcategoryId === sub.id || p.subcategory === sub.name).length;
                  return (
                    <FreshFlowerCard 
                      key={sub.id} 
                      subcategory={sub} 
                      productCount={count} 
                      onClick={handleFreshFlowerClick} 
                      isMobile={isMobile} 
                    />
                  );
                })}
              </div>
            </div>
          )}
          
          {newArrivalProducts.length > 0 && (
            <HorizontalProductSection 
              title="New Arrivals" 
              products={newArrivalProducts} 
              onAddToCart={handleAddToCart} 
              onBuyNow={handleBuyNow} 
              isMobile={isMobile} 
            />
          )}
          
          {/* Decoration Gallery Section */}
{galleryImages.length > 0 && (
  <div style={{ background: 'white', borderRadius: '16px', padding: isMobile ? '14px' : '20px', marginBottom: '20px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
      <h3 style={{ fontSize: isMobile ? '14px' : '18px', fontWeight: 700, color: '#1a0a0a' }}>🎨 Decoration Gallery</h3>
      <button 
        onClick={handleDecorationGallery} 
        style={{ color: '#dc2626', fontSize: isMobile ? '11px' : '13px', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}
      >
        View Full Gallery →
      </button>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '12px' }}>
      {galleryImages.map((image, idx) => {
        // Get the image URL - handle both old and new format
        let imageUrl = null;
        let imageTitle = image.title || 'Decoration';
        
        if (image.images && image.images.length > 0) {
          // New format: images array
          imageUrl = image.images[0].url;
          imageTitle = image.title || image.images[0].title || 'Decoration';
        } else if (image.url) {
          // Old format: single url
          imageUrl = image.url;
        } else if (image.image) {
          imageUrl = image.image;
        }
        
        if (!imageUrl) return null;
        
        return (
          <div 
            key={image.id || idx}
            onClick={handleDecorationGallery}
            style={{ 
              borderRadius: '12px', 
              overflow: 'hidden', 
              cursor: 'pointer',
              position: 'relative',
              aspectRatio: '4/3',
              background: '#f8fafc',
              transition: 'transform 0.2s, boxShadow 0.2s',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
            }}
          >
            <img 
              src={imageUrl} 
              alt={imageTitle} 
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1591886960571-74d43a9d4166?w=400&q=80';
              }}
            />
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
              padding: '8px 6px',
              color: 'white',
              fontSize: isMobile ? '10px' : '12px',
              fontWeight: 500
            }}>
              {imageTitle}
            </div>
            {image.images && image.images.length > 1 && (
              <div style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                background: 'rgba(0,0,0,0.6)',
                color: 'white',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '10px',
                fontWeight: 500
              }}>
                {image.images.length} photos
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
)}
        </div>
      ) : (
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '16px', minHeight: '60vh', paddingBottom: isMobile ? '70px' : '0' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '20px', 
            background: 'white', 
            padding: '14px 18px', 
            borderRadius: '12px',
            border: '1px solid rgba(220,38,38,0.1)'
          }}>
            <div>
              <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#1a0a0a' }}>Search Results</h2>
              <p style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                {searchResults.length} products found for "{searchQuery}"
              </p>
            </div>
            <button 
              onClick={clearSearch} 
              style={{ 
                padding: '8px 16px', 
                background: '#f1f5f9', 
                border: 'none', 
                borderRadius: '8px', 
                fontSize: '12px', 
                cursor: 'pointer',
                color: '#64748b'
              }}
            >
              Clear
            </button>
          </div>
          
          {searchResults.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              {searchResults.slice(0, 20).map(p => (
                <div key={p.id} style={{ width: isMobile ? 'calc(50% - 8px)' : 'calc(25% - 12px)' }}>
                  <HorizontalProductCard 
                    product={p} 
                    onAddToCart={handleAddToCart} 
                    onBuyNow={handleBuyNow} 
                  />
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '60px 20px', background: 'white', borderRadius: '16px' }}>
              <Search size={48} color="#cbd5e1" />
              <h3 style={{ fontSize: '16px', marginTop: '16px', color: '#1a0a0a' }}>No products found</h3>
              <p style={{ color: '#64748b', marginTop: '8px' }}>Try searching with different keywords</p>
              <button 
                onClick={clearSearch} 
                style={{ 
                  marginTop: '20px', 
                  padding: '10px 24px', 
                  background: '#dc2626', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '8px', 
                  cursor: 'pointer', 
                  fontWeight: 600 
                }}
              >
                Browse All Products
              </button>
            </div>
          )}
        </div>
      )}
      
      <Footer />
      
      {/* Bottom Navigation - Mobile Only */}
      <div style={{ 
        display: isMobile ? 'flex' : 'none', 
        position: 'fixed', 
        bottom: 0, 
        left: 0, 
        right: 0, 
        background: 'white',
        padding: '8px 12px',
        paddingBottom: 'max(10px, env(safe-area-inset-bottom))',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 999,
        boxShadow: '0 -4px 20px rgba(0,0,0,0.08)',
        borderTop: '1px solid rgba(220,38,38,0.15)'
      }}>
        <button 
          onClick={() => { clearSearch(); window.scrollTo({ top: 0, behavior: 'smooth' }); navigate('/'); }} 
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: '4px', 
            background: 'none', 
            border: 'none', 
            cursor: 'pointer', 
            padding: '6px 12px', 
            borderRadius: '10px', 
            transition: 'all 0.2s', 
            color: window.location.pathname === '/' ? '#dc2626' : '#64748b',
            flex: 1
          }}
        >
          <Home size={22} />
          <span style={{ fontSize: '10px', fontWeight: 500 }}>Home</span>
        </button>

        <button 
          onClick={() => setShowCart(true)} 
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: '4px', 
            background: 'none', 
            border: 'none', 
            cursor: 'pointer', 
            position: 'relative', 
            padding: '6px 12px', 
            borderRadius: '10px', 
            transition: 'all 0.2s', 
            color: '#64748b',
            flex: 1
          }}
        >
          <ShoppingCart size={22} />
          {cart.length > 0 && (
            <span style={{ 
              position: 'absolute', 
              top: '-4px', 
              right: '20%', 
              background: '#dc2626', 
              color: 'white', 
              fontSize: '9px', 
              fontWeight: 700, 
              minWidth: '18px', 
              height: '18px', 
              borderRadius: '50%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              {cart.length}
            </span>
          )}
          <span style={{ fontSize: '10px', fontWeight: 500 }}>Cart</span>
        </button>

        <button 
          onClick={handleDecorationEnquiry} 
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: '4px', 
            background: 'none', 
            border: 'none', 
            cursor: 'pointer', 
            padding: '6px 12px', 
            borderRadius: '10px', 
            transition: 'all 0.2s', 
            color: '#64748b',
            flex: 1
          }}
        >
          <Palette size={22} />
          <span style={{ fontSize: '10px', fontWeight: 500 }}>Enquiry</span>
        </button>

        <button 
          onClick={handleBottomProfile} 
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: '4px', 
            background: 'none', 
            border: 'none', 
            cursor: 'pointer', 
            padding: '6px 12px', 
            borderRadius: '10px', 
            transition: 'all 0.2s', 
            color: user ? '#dc2626' : '#64748b',
            flex: 1
          }}
        >
          <User size={22} />
          <span style={{ fontSize: '10px', fontWeight: 500 }}>{user ? 'Profile' : 'Login'}</span>
        </button>
      </div>
      
      {/* Modals */}
      {showCart && <CartDrawer onClose={() => setShowCart(false)} onCheckout={() => { setShowCart(false); if(!user){ navigate('/login'); return; } setShowCheckout(true); }} />}
      {showCheckout && <CheckoutPage onClose={() => setShowCheckout(false)} onPlaceOrder={handlePlaceOrder} />}
      {showProfile && <ProfilePage onClose={() => setShowProfile(false)} defaultTab={profileDefaultTab} />}
      {showDecorationEnquiry && <DecorationEnquiryPage onClose={() => setShowDecorationEnquiry(false)} />}
      
      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .spinner { animation: spin 0.6s linear infinite; }
      `}</style>
    </div>
  );
};

export default Website;